'use client';

import type { LngLat } from '@ahm/api-wrappers-core';
import { Group, Stack, Title, Text, Box } from '@mantine/core';
import { type SVGProps, useEffect, useMemo, useState } from 'react';
import {
  Marker,
  type MarkerDragEvent,
  Popup,
  useMap,
} from 'react-map-gl/maplibre';
import './animations.css';
import LottieAnimation from './lib/lottie-animation';
import animationData from './lib/pickup-indicator.json';
import { createTranslations, locales } from './locales';
import AnchorBox from './markers/anchor-box';
import useMarkers from './markers/use-marker';
import { useMapControl } from './store';
import type { PathMarkerProps } from './types';

const SvgWarning = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1rem"
    height="1rem"
    fill="none"
    viewBox="0 0 32 32"
    {...props}
  >
    <rect width={32} height={32} fill="#FFF1F1" rx={16} />
    <path
      fill="#DA1E28"
      d="M16 9.5a6.5 6.5 0 1 0 6.5 6.5A6.507 6.507 0 0 0 16 9.5m2.5 7h-5a.5.5 0 0 1 0-1h5a.5.5 0 0 1 0 1"
    />
  </svg>
);

export function PathMarker({
  showPopup = false,
  disabledPopup = false,
  isActive = false,
  pathWithIndexes: path,
  enableBroadcasting,
  pathsLength = 0,
  iconType = 'booking',
  lang = 'vi',
  pathIndex = [],
  onMarkerDragEnd,
  isFlyTo = false,
  ...markerProps
}: PathMarkerProps) {
  const t = createTranslations({ locales, lang });
  const [isHover, setIsHover] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [markerPosition, setMarkerPosition] = useState<LngLat>({
    lat: path.lat,
    lng: path.lng,
  });

  const nearbyEntrances = useMapControl.use.nearbyEntrances();

  const entranceRestricted = useMemo(() => {
    return nearbyEntrances.find(
      (entrance) =>
        entrance.coordinates[0] === path.lat &&
        entrance.coordinates[1] === path.lng &&
        entrance.message &&
        !entrance.enabled
    );
  }, [path.lat, path.lng, nearbyEntrances]);

  const { current: map } = useMap();

  const isMove = isActive || isHover;

  const { getSingleTypeIcon, getMultiTypeIcon } = useMarkers(isMove);

  const popUpLabel = useMemo(() => {
    if (path.indexes.length > 1) {
      return `<strong>${t('point')}</strong>: ${path.indexes.join(', ')}\n<strong>${t('address')}</strong>: ${path.first_label ?? path.address}`;
    }

    return path.first_label ?? path.address;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const popupLabelComponent = () => {
    if (!entranceRestricted) {
      return (
        <Box
          style={
            iconType === 'supplier-location' || path.indexes.length > 1
              ? {
                  whiteSpace: 'pre-line',
                }
              : {
                  overflowX: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
          }
          title={path.address}
          dangerouslySetInnerHTML={{
            __html: popUpLabel,
          }}
        />
      );
    }
    return (
      <Group gap={8}>
        <SvgWarning className="size-8" />
        <Stack gap={0}>
          <Title order={5} fw={700} fz={12}>
            {entranceRestricted?.name}
          </Title>
          <Text fz={12} fw={500}>
            {entranceRestricted?.message}
          </Text>
        </Stack>
      </Group>
    );
  };

  function renderMarker() {
    const payload = { path, pathsLength, type: iconType };
    if (path.point_type) {
      return getMultiTypeIcon(payload);
    }
    return getSingleTypeIcon(payload);
  }

  const handleMouseEvent = () => {
    setIsHover((prev) => !prev);
  };

  const handleMouseClick = () => {
    if (iconType !== 'supplier-location') {
      return;
    }

    setIsClicked((prev) => !prev);
  };

  const handleMarkerDragEnd = (event: MarkerDragEvent) => {
    const { lat, lng } = event.lngLat;

    setMarkerPosition({ lat, lng });

    if (onMarkerDragEnd) {
      onMarkerDragEnd(lat, lng);
    }
  };

  useEffect(() => {
    setMarkerPosition({ lat: path.lat, lng: path.lng });
  }, [path.lat, path.lng]);

  useEffect(() => {
    if (isFlyTo && map) {
      map.flyTo({
        center: [markerPosition.lng, markerPosition.lat],
        speed: 0.8,
        curve: 1.42,
        essential: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFlyTo, map, markerPosition]);

  if (
    pathIndex.length > 0 &&
    path.indexes.some((idx) => !pathIndex.includes(idx))
  ) {
    return null;
  }

  return (
    <>
      {enableBroadcasting && path.indexes[0] === 0 ? (
        <Marker
          anchor="center"
          latitude={path.lat}
          longitude={path.lng}
          style={{
            opacity: 0.75,
          }}
        >
          <LottieAnimation
            animationData={animationData}
            height="256px"
            width="256px"
          />
        </Marker>
      ) : null}

      <Marker
        anchor="bottom"
        latitude={markerPosition?.lat}
        longitude={markerPosition?.lng}
        onDragEnd={handleMarkerDragEnd}
        {...markerProps}
        onClick={handleMouseClick}
        style={{ zIndex: 5 }}
      >
        <div onMouseEnter={handleMouseEvent} onMouseLeave={handleMouseEvent}>
          {renderMarker()}
          {isMove ? <AnchorBox /> : null}
        </div>
      </Marker>
      {!disabledPopup &&
      (showPopup || isMove || isClicked) &&
      iconType !== 'pin' ? (
        <Popup
          anchor="bottom-left"
          latitude={path.lat}
          longitude={path.lng}
          closeButton={false}
          closeOnClick={false}
        >
          {popupLabelComponent()}
        </Popup>
      ) : null}
    </>
  );
}
